// ./src/components/features/Billing/TopUpDrawer.tsx
//
// Buying more sending allowance, paid for RIGHT HERE.
//
// ⚠ IT LIVES IN `features/Billing` BECAUSE IT BELONGS TO NEITHER OF ITS CALLERS. It is opened
// from the message composer (buying account-wide, mid-send) and from the event dashboard
// (buying for one event) — two unrelated features — and copying it into the second would have
// forked the payment flow that actually takes money. The glue that drives it is `useTopUpDrawer`,
// for the same reason: the copyable part of a feature is never the component, it is the wiring.
//
// ⚠ WHY A DRAWER AND NOT A REDIRECT — the single most important thing about this file. This
// opens over a half-composed send: recipients picked, message written, preview generated. The
// first version sent the host to Stripe's hosted checkout, which navigates the app away and
// destroys all of it. Buying more allowance has to cost nothing except the money.
//
// So payment happens inline with Stripe's PaymentElement, exactly as guests buy tickets
// (`StripePayStep` in events-web), with `redirect: 'if_required'` so card payments resolve
// without ever leaving. When it closes, the composer is exactly as it was — with a bigger
// balance.
//
// ⚠ DELIBERATELY NOT A URL STAGE, unlike every other drawer in this app. A payment in flight
// must not be resumable from a cold link: a `clientSecret` is single-use and tied to one
// intent, so a reload that "reopened" the payment would present a dead or duplicate form. The
// house rule exists so a reload restores what you were doing; here the right restoration is
// the composer, not the card form.
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Group, Loader, Stack, Text, TextInput } from "@mantine/core";
import { Elements } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { formatWithCurrencyLabel } from "@em/utils";

import { AppDrawer, AppIcon } from "@/components/generic";
import { AppAddonProduct } from "@/utils/api/AppAddonService";
import { AppliedDiscount, TopUpPhase } from "@/hooks/useTopUp";
import { useFormSnapshot } from "@/hooks";
import { TopUpKind } from "@/utils/topUp";

import { getPlatformStripe, PlatformPayStep } from "./PlatformPayStep";

/** How long a payment may confirm before the drawer can be closed again. */
const PAY_STALL_MS = 60_000;

const formatPrice = (bundle: AppAddonProduct): string | null => {
  if (bundle.amount == null || !bundle.currency) {
    return null;
  }

  // ⚠ It says WHICH currency ("US$29.00", never a bare "$" — eight currencies write one), and this is the number about
  //   to be charged.
  return formatWithCurrencyLabel(new Intl.NumberFormat(undefined, { style: "currency", currency: bundle.currency }), bundle.amount, bundle.currency);
};

export interface TopUpDrawerProps {
  opened: boolean;
  onClose: () => void;
  /**
   * ⚠ TWO *SENDING* COUNTERS, NOT THREE — and this prop is typed to make that unforgettable. It
   * used to be the CHANNEL (`email | sms | whatsapp`), which implied a WhatsApp wallet that has
   * never existed: the server does `kind = channel === 'email' ? 'email' : 'message'`, so SMS
   * and WhatsApp both spend `message_credits`. The dashboard's old modal sold "SMS credits" off
   * exactly that misreading. Callers map their channel to a kind; the drawer never sees one.
   *
   * ⚠ `ai` is in the union but is NOT a sending counter — it is `ai_credits`, a separate
   * feature sold from the Subscription page. Adding it did not make a third messaging wallet.
   */
  kind: TopUpKind;
  /**
   * Which catalog is on screen. `services` sells things a HUMAN delivers — a call, a setup,
   * concierge hours — bought through the identical rail as a credit bundle.
   *
   * ⚠ Drives only the WORDS and the ownership state, never the purchase. A service and a
   * bundle are the same transaction to every layer below this one, which is precisely why
   * nothing else in the payload could distinguish them.
   */
  mode?: "credits" | "services";
  /**
   * Whether this was opened from INSIDE another task the host would hate to lose (the message
   * composer). Drives only the reassurance line — see its comment.
   */
  inTask?: boolean;
  bundles: AppAddonProduct[];
  loadingBundles: boolean;
  /**
   * Purchased credits held for this kind. `undefined` = NOT KNOWN — render nothing, never "0".
   *
   * ⚠ Separate from `balance` because `balance` is event-relative and reports only the two
   * SENDING counters. AI showed no figure at all until this existed, while its credits sat in
   * the wallet the whole time.
   */
  purchasedCredits?: number;
  phase: TopUpPhase;
  arrivedCredits: number;
  errorText: string | null;
  /**
   * Why the last attempt to START a purchase was refused — most often a discount code.
   *
   * ⚠ SEPARATE FROM `errorText`, which is a PAYMENT failure shown beside the card form. This one
   * belongs on the bundle step, because that is where the host still has the code in front of
   * them and can fix it. Without it the server's carefully-worded refusals ("that code has
   * expired", "that code does not apply to this bundle") are computed, logged, returned — and
   * then silently dropped, leaving the drawer to bounce back to the list explaining nothing.
   */
  purchaseError: string | null;
  clientSecret?: string | null;
  publishableKey?: string | null;
  amountLabel: string | null;
  /** The code the host has typed. Controlled by `useTopUpDrawer`, which owns it across steps. */
  discountCode: string;
  onDiscountCodeChange: (code: string) => void;
  /**
   * What the server ACCEPTED, once a purchase has been started. Null until then.
   *
   * ⚠ There is deliberately no "check this code" button and no client-side validation. A code is
   * validated by the server as part of starting the purchase, because the server is the only
   * thing that can price it — anything here would be a second opinion that eventually disagrees
   * with the authority.
   */
  discount: AppliedDiscount | null;
  /** The bundle the host has picked, if any. Selection and purchase are separate steps. */
  selectedSku: string | null;
  onSelect: (sku: string) => void;
  /** The footer's primary action — starts the purchase for the selected bundle + typed code. */
  onBuy: () => void;
  onPaid: () => void;
  onError: (message: string | null) => void;
  onBack: () => void;
}

export const TopUpDrawer: React.FC<TopUpDrawerProps> = ({
  opened,
  onClose,
  kind,
  mode = "credits",
  inTask = false,
  bundles,
  loadingBundles,
  purchasedCredits,
  phase,
  arrivedCredits,
  errorText,
  purchaseError,
  clientSecret,
  publishableKey,
  amountLabel,
  discountCode,
  onDiscountCodeChange,
  discount,
  selectedSku,
  onSelect,
  onBuy,
  onPaid,
  onError,
  onBack,
}) => {
  const { t } = useTranslation(["events", "common"]);
  const services = mode === "services";
  // What one credit buys, in the plural — used in "1,000 emails added" and the balance line.
  const unitWord = services
    ? "services"
    : kind === "email"
      ? "emails"
      : kind === "ai"
        ? "AI actions"
        : "messages";

  /**
   * The bundle-picking step: idle, with bundles on screen. Named once here rather than tested
   * inline, because the footer and the body must agree about which step they are on — two
   * separate conditions drifting apart is how a drawer ends up offering Buy over a card form.
   */
  const picking = phase === "idle" && !loadingBundles && bundles.length > 0;

  const stripePromise = useMemo(
    () => (publishableKey ? getPlatformStripe(publishableKey) : null),
    [publishableKey]
  );

  // The bundle-picking step is a form (a pick + a typed code), so Esc / a click outside / Cancel ask before discarding
  // it (#123). Seeded from what the drawer opens with — `useTopUpDrawer` resets both on every open.
  const { isDirty, seedFrom, clear } = useFormSnapshot({ selectedSku, discountCode });
  // The PAYING step's work is the card form (#124): Stripe's element, read through its `onChange`. A new payment intent
  // mounts a fresh, empty element, so both reset with it.
  const [cardTyped, setCardTyped] = useState(false);
  const [paySubmitting, setPaySubmitting] = useState(false);
  // Stripe has not answered for a minute (a stalled network): hand the drawer back rather than hold it for good (#126).
  const [payStalled, setPayStalled] = useState(false);

  useEffect(() => {
    setCardTyped(false);
    setPaySubmitting(false);
  }, [clientSecret, phase]);

  useEffect(() => {
    setPayStalled(false);

    if (!paySubmitting) {
      return undefined;
    }

    const timer = setTimeout(() => setPayStalled(true), PAY_STALL_MS);

    return () => clearTimeout(timer);
  }, [paySubmitting]);

  useEffect(() => {
    if (!opened) {
      clear();

      return;
    }

    seedFrom({ selectedSku, discountCode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const body = () => {
    if (phase === "arrived") {
      return (
        <Alert variant="light" color="teal" radius="md" icon={<AppIcon iconName="Check" size={18} />}>
          <Text size="md" fw={600}>
            {`${arrivedCredits.toLocaleString()} ${unitWord} added`}
          </Text>
          <Text size="md">
            {t("events:composer.topUp.arrivedBody", {
              defaultValue:
                "They are used before your monthly allowance. Close this and carry on with your send.",
            })}
          </Text>
        </Alert>
      );
    }

    if (phase === "slow") {
      return (
        <Alert variant="light" color="yellow" radius="md" icon={<AppIcon iconName="Clock" size={18} />}>
          <Text size="md" fw={600}>
            {t("events:composer.topUp.slowTitle", { defaultValue: "Your payment went through" })}
          </Text>
          <Text size="md">
            {t("events:composer.topUp.slowBody", {
              defaultValue:
                "The credits are taking a little longer to appear than usual. Nothing has been lost — they will show up shortly.",
            })}
          </Text>
        </Alert>
      );
    }

    if (phase === "confirming") {
      return (
        <Group gap="sm">
          <Loader size="sm" />
          <Text size="md">
            {t("events:composer.topUp.confirming", { defaultValue: "Payment received — adding your credits…" })}
          </Text>
        </Group>
      );
    }

    if (phase === "paying" && clientSecret && stripePromise) {
      return (
        <Stack gap="md">
          {/* ⚠ Says what closing now means: the charge may still land, and the credits with it — never "nothing was charged". */}
          {payStalled && (
            <Alert variant="light" color="yellow" radius="md" icon={<AppIcon iconName="Clock" size={18} />}>
              <Text size="md">
                {t("events:composer.topUp.payStalled", {
                  defaultValue:
                    "The payment is taking longer than usual. You can close this — if the payment goes through, your credits are added on their own.",
                })}
              </Text>
            </Alert>
          )}

          {errorText && (
            <Alert variant="light" color="red" radius="md" icon={<AppIcon iconName="AlertCircle" size={18} />}>
              <Text size="md">{errorText}</Text>
            </Alert>
          )}

          {/* ⚠ EXPLAINS WHY THE TOTAL DIFFERS FROM THE LISTED PRICE. The pay button already
              carries the discounted figure; without this line a host sees a bundle priced at one
              number and a button asking for another, which reads as a bug at the exact moment
              they are deciding whether to trust us with a card. */}
          {discount && (
            <Alert variant="light" color="teal" radius="md" icon={<AppIcon iconName="Discount" size={18} />}>
              <Text size="md">
                {t("events:composer.topUp.discountApplied", {
                  defaultValue: "Code {{code}} applied — {{off}} off.",
                  code: discount.code,
                  off: formatWithCurrencyLabel(
                    new Intl.NumberFormat(undefined, { style: "currency", currency: discount.currency }),
                    discount.discountAmount,
                    discount.currency,
                  ),
                })}
              </Text>
            </Alert>
          )}

          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PlatformPayStep
              amountLabel={amountLabel}
              onPaid={onPaid}
              onError={onError}
              onBack={onBack}
              onCardTyped={setCardTyped}
              onSubmittingChange={setPaySubmitting}
            />
          </Elements>
        </Stack>
      );
    }

    if (loadingBundles || phase === "creating") {
      return (
        <Group gap="sm">
          <Loader size="sm" />
          <Text size="md" c="dimmed">
            {t("events:composer.topUp.loading", { defaultValue: "Loading bundles…" })}
          </Text>
        </Group>
      );
    }

    if (bundles.length === 0) {
      return (
        <Text size="md" c="dimmed">
          {t("events:composer.topUp.none", {
            defaultValue: "No top-up bundles are available on this account right now.",
          })}
        </Text>
      );
    }

    return (
      <Stack gap="sm">
        {/* ⚠ THE REFUSAL LANDS HERE, NOT BESIDE THE CARD FORM. A rejected discount code never
            reaches the payment step — it fails while the intent is being created — so this is
            the only screen on which the host can act on it. The server's wording is shown
            verbatim because it names WHICH rule failed, and "expired" and "not for this bundle"
            need completely different responses from the person reading them. */}
        {purchaseError && (
          <Alert variant="light" color="red" radius="md" icon={<AppIcon iconName="AlertCircle" size={18} />}>
            <Text size="md">{purchaseError}</Text>
          </Alert>
        )}

        {/* ⚠ THERE IS NO SCOPE NOTICE HERE ANY MORE, AND ITS ABSENCE IS THE FEATURE (A2.9.2).
            This drawer used to warn that credits bought from an event dashboard were locked to
            that event — true at the time, and the disclosure was load-bearing because the
            Subscription page did not count them. Every purchase is account-wide now, so there
            is nothing to disclose: the thing being bought is the thing a host would assume. */}
        {/* ⚠ Hidden when UNKNOWN (undefined), not when zero — and never rendered as "0 left"
            before the wallet read lands. Telling a host with 29,899 emails that they have none
            invites them to buy the same thing twice.
            ⚠ Not shown for services either: "you have 2 purchased services left" is not what a
            service balance means, and each service reports its own ownership on its row. */}
        {!services && purchasedCredits != null && purchasedCredits > 0 && (
          <Text size="md" c="dimmed">
            {t("events:composer.topUp.currentBalance", {
              defaultValue: "You have {{count}} purchased {{unit}} left. Bundles never expire and are used first.",
              count: purchasedCredits,
              unit: unitWord,
            })}
          </Text>
        )}

        {/* ⚠ THE ROWS SELECT; THEY NO LONGER BUY. A Buy button per row committed the purchase
            before the host could reach the discount field below it, which made the field
            unreachable in the obvious flow — pick, then type, then buy. One primary action in
            the footer is also the only arrangement where the code can affect the price, because
            the code has to be in hand at the moment the purchase starts.

            ⚠ Radio semantics, not a checkbox: exactly one bundle, and `radiogroup`/`radio` roles
            plus `aria-checked` so this is operable and announced as a single choice. A row of
            clickable Cards with no roles is a mouse-only control. */}
        <Stack gap="sm" role="radiogroup" aria-label={t("events:composer.topUp.bundlesLabel", {
          defaultValue: "Choose a bundle",
        })}>
          {bundles.map((bundle) => {
            const price = formatPrice(bundle);
            const sku = String(bundle.providerSku ?? "");
            // ⚠ Not purchasable = no price in this Stripe mode. Shown and labelled, never
            // hidden and never selectable.
            const owned = !!bundle.limitReached;
            const disabled = !bundle.purchasable || !price;
            const selected = !disabled && sku === selectedSku;

            return (
              <Card
                key={sku}
                withBorder
                radius="md"
                padding="sm"
                role="radio"
                aria-checked={selected}
                aria-disabled={disabled || undefined}
                tabIndex={disabled ? -1 : 0}
                onClick={() => !disabled && onSelect(sku)}
                onKeyDown={(e) => {
                  // Space/Enter pick the focused row, as a radio does.
                  if (!disabled && (e.key === " " || e.key === "Enter")) {
                    e.preventDefault();
                    onSelect(sku);
                  }
                }}
                style={{
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.6 : 1,
                  // ⚠ Selection is shown by BORDER AND BACKGROUND, not colour alone — the
                  // accent on its own is invisible to anyone who cannot separate it from the
                  // default border, and this is the control that decides what gets charged.
                  borderColor: selected ? "var(--mantine-primary-color-filled)" : undefined,
                  borderWidth: selected ? 2 : undefined,
                  background: selected ? "var(--mantine-primary-color-light)" : undefined,
                }}
              >
                <Group justify="space-between" wrap="nowrap" gap="md">
                  <Stack gap={2}>
                    <Text size="md" fw={600}>
                      {bundle.name}
                    </Text>
                    <Text size="md" c="dimmed">
                      {/* ⚠ ALREADY OWNED IS SAID FIRST, and it is not the same sentence as "no
                          price yet". Someone who bought the onboarding call is looking at
                          something they have, not at something that is broken — telling them it
                          is "not available" would send them to support over a success. */}
                      {owned
                        ? t("events:composer.topUp.alreadyPurchased", { defaultValue: "Already purchased" })
                        : price ??
                          t("events:composer.topUp.unavailable", { defaultValue: "Not available for purchase yet" })}
                    </Text>
                  </Stack>

                  {selected && <AppIcon iconName="CircleCheck" size={22} />}
                </Group>
              </Card>
            );
          })}
        </Stack>

        {/* ⚠ THE CODE IS TYPED BEFORE A BUNDLE IS CHOSEN, and validated when one is. There is
            no "Apply" button on purpose: a code's validity depends on the BUNDLE (a coupon can
            be restricted to certain products, and can have a minimum spend), so "valid" is not
            a property of the code alone and a tick here would sometimes be a lie. Pressing Buy
            is what asks, and a refusal names its reason. */}
        <TextInput
          label={t("events:composer.topUp.discountLabel", { defaultValue: "Discount code (optional)" })}
          placeholder={t("events:composer.topUp.discountPlaceholder", { defaultValue: "Enter a code" })}
          description={t("events:composer.topUp.discountHint", {
            defaultValue: "Checked when you press Buy — the price shown above is before any discount.",
          })}
          value={discountCode}
          onChange={(e) => onDiscountCodeChange(e.currentTarget.value)}
          // ⚠ Enter buys, but ONLY with a bundle picked — otherwise pressing it after typing a
          // code would do nothing with no explanation. Guarded here as well as in `onBuy`,
          // because a silent no-op on Enter reads as a broken field.
          onKeyDown={(e) => {
            if (e.key === "Enter" && selectedSku) {
              e.preventDefault();
              onBuy();
            }
          }}
          // Codes are case-sensitive at Stripe, so this must NOT uppercase or trim mid-typing —
          // the server trims once, and nothing else touches what the host entered.
          autoComplete="off"
          spellCheck={false}
        />

        {/* ⚠ Says the send is safe. A host mid-compose needs to know that paying here will not
            cost them the message they have written — which is exactly what the old redirect
            version did. */}
        {/* ⚠ Only true where there IS work to lose. On the event dashboard nothing is
            half-composed, and promising to preserve "your recipients and message" there would
            be meaningless noise about a task the host is not doing. The generic line still
            makes the important promise: the page does not navigate away. */}
        <Text size="xs" c="dimmed">
          {inTask
            ? t("events:composer.topUp.staysHere", {
                defaultValue: "You pay here — your recipients and message are kept exactly as they are.",
              })
            : t("events:composer.topUp.staysHereGeneric", {
                defaultValue: "You pay here — you won't leave this page.",
              })}
        </Text>
      </Stack>
    );
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={
        services
          ? t("events:composer.topUp.titleServices", { defaultValue: "Get help from us" })
          : kind === "email"
            ? t("events:composer.topUp.titleEmail", { defaultValue: "Buy more email allowance" })
            : kind === "ai"
              ? t("events:composer.topUp.titleAi", { defaultValue: "Buy more AI actions" })
              : t("events:composer.topUp.titleMessage", { defaultValue: "Buy more message allowance" })
      }
      /**
       * ⚠ THE FOOTER ACTION EXISTS ONLY ON THE BUNDLE-PICKING STEP, and that is deliberate
       * rather than an oversight on the others:
       *   • paying — the Pay button MUST live inside `<Elements>` (it needs the Stripe hooks),
       *     so a second primary in the footer would be a dead twin of the real one.
       *   • creating / confirming / arrived / slow — there is nothing to confirm; the purchase
       *     is either in flight or finished, and a Buy button there would invite a double
       *     purchase at the exact moment one is already running.
       * Every other step therefore falls back to a plain Close.
       */
      actionType={picking ? "confirm-cancel" : "close"}
      onConfirm={onBuy}
      confirmLabel={
        amountLabel
          ? t("events:composer.topUp.buyAmount", { defaultValue: "Buy {{amount}}", amount: amountLabel })
          : t("events:composer.topUp.buy", { defaultValue: "Buy" })
      }
      confirmIcon="CreditCard"
      // ⚠ Disabled until a bundle is picked. `onBuy` refuses without one anyway — a disabled
      // prop is a UI state, not a rule, and this is the call that takes money.
      confirmDisabled={!selectedSku}
      // The picking step's pick + code, and the paying step's card details; the other steps are a purchase in flight or
      // finished. While a payment confirms, nothing closes the drawer — its answer would land on nothing.
      isDirty={(picking && isDirty) || (phase === "paying" && cardTyped)}
      closable={!paySubmitting || payStalled}
      size="md"
    >
      {body()}
    </AppDrawer>
  );
};
